import { makeAutoObservable, runInAction } from 'mobx';
import { IShop, IShopTag } from '../../models/User';
import PortalService from '../../services/api/user-service';
import { rootContainer, TYPE } from '../../services/container/root';

const SEARCH_KEY_MIN_LENGTH = 3;

interface IShopWithTag extends IShopTag, IShop { }

class ShopsSelectorStore {
    private _isProcessing: boolean = false;
    private _fetchedShops: IShop[] = [];
    private _selectedShops: IShop[] = [];
    private _searchKey: string = '';

    constructor(
        private onChange: (list: IShop[]) => void, 
        private getShopTag?: (shop: IShop) => IShopTag, 
        private readonly includeSecondaryPortals: boolean = false) 
    {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get isProcessing(): boolean {
        return this._isProcessing;
    }

    public get fetchedShops(): IShopWithTag[] {
        return this._fetchedShops.map(s => {
            const tagData = this.getShopTag ? this.getShopTag(s) : {
                isSelectable: true,
                tag: ''
            }
            return {
                ...s,
                ...tagData
            }
        });
    }

    public get selectedShops(): IShop[] {
        return this._selectedShops;
    }

    public get searchKey(): string {
        return this._searchKey;
    }

    public setSearchKey(value: string) {
        this._searchKey = value;
        if (this._searchKey.length < SEARCH_KEY_MIN_LENGTH) {
            this._fetchedShops = [];
        }
    }

    public setSelected(list: IShop[]) {
        this._selectedShops = list;
    }

    public unselect(item: IShop) {
        this._selectedShops = this._selectedShops?.filter(i => i.id !== item.id);
        this.onChange(this._selectedShops);
        this._searchKey = '';
    }

    public select(item: IShop) {
        this._selectedShops = [...this._selectedShops, item];
        this._fetchedShops = [];
        this._searchKey = '';
        this.onChange(this._selectedShops);
    }

    private get service(): PortalService {
        return rootContainer.get<PortalService>(TYPE.UserService);
    }

    public async searchShops() {
        if (this._searchKey.length < SEARCH_KEY_MIN_LENGTH) {
            return;
        }
        this._isProcessing = true;
        let result = await this.service.searchShops(this._searchKey, this.includeSecondaryPortals);
        // remove items that were already selected
        result = result.filter(i => {
            return this._selectedShops ? this._selectedShops?.findIndex(si => si.id === i.id) === -1 : true;
        });
        runInAction(() => {
            this._fetchedShops = result;
            this._isProcessing = false;
        });
    }
}

export default ShopsSelectorStore;
